// React hooks for the AlphaMind IDO Frontend logging system
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { LoggingContext } from '../contexts/LoggingContext';
import { LoggingService } from '../services/logging.service';
import {
  WalletType,
  ValidationError,
  NetworkError,
  WalletConnectionLoggerHook,
  PurchaseLoggerHook,
  ErrorLoggerHook,
  UserActionLoggerHook,
  PageViewLoggerHook,
  IdoLoggerHook,
  AuthLoggerHook,
  NetworkLoggerHook
} from '../types/logging.types';

// Base hook to get logging service
export const useLogger = (): LoggingService => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLogger must be used within a LoggingProvider');
  }
  return context.loggingService;
};

// Wallet connection logging hook
export const useWalletConnectionLogger = (): WalletConnectionLoggerHook => {
  const logger = useLogger();

  const logConnectionAttempt = useCallback((
    walletType: WalletType, 
    data?: Record<string, any>
  ) => {
    logger.logConnectionAttempt(walletType, data);
  }, [logger]);

  const logConnectionSuccess = useCallback((
    walletType: WalletType, 
    data?: Record<string, any>
  ) => {
    const address = data?.address || data?.walletAddress;
    logger.logConnectionSuccess(walletType, address, data);
  }, [logger]);

  const logConnectionError = useCallback((
    walletType: WalletType, 
    error: Error, 
    data?: Record<string, any>
  ) => {
    logger.logConnectionError(walletType, error, data);
  }, [logger]);

  const logProviderConflict = useCallback((
    conflictingProviders: string[], 
    data?: Record<string, any>
  ) => {
    logger.logProviderConflict(conflictingProviders, data);
  }, [logger]);

  const logWalletDetected = useCallback((
    walletType: WalletType, 
    data?: Record<string, any>
  ) => {
    logger.logWalletConnection(walletType, 'wallet_detected' as any, data);
  }, [logger]);

  return {
    logConnectionAttempt,
    logConnectionSuccess,
    logConnectionError,
    logProviderConflict,
    logWalletDetected
  };
};

// Authentication logging hook
export const useAuthLogger = (): AuthLoggerHook => {
  const logger = useLogger();

  const logLoginAttempt = useCallback((
    walletType: WalletType, 
    data?: Record<string, any>
  ) => {
    logger.logLoginAttempt(walletType, data);
  }, [logger]);

  const logLoginSuccess = useCallback((
    walletType: WalletType, 
    userId: string, 
    data?: Record<string, any>
  ) => {
    logger.logLoginSuccess(walletType, userId, data);
  }, [logger]);

  const logLoginError = useCallback((
    walletType: WalletType, 
    error: Error, 
    data?: Record<string, any>
  ) => {
    logger.logLoginError(walletType, error, data);
  }, [logger]);

  const logLogout = useCallback((
    userId: string, 
    data?: Record<string, any>
  ) => {
    logger.logAuthentication('logout' as any, undefined, { userId, ...data });
  }, [logger]);

  const logMessageSign = useCallback((
    walletType: WalletType, 
    success: boolean, 
    data?: Record<string, any>
  ) => {
    logger.logMessageSign(walletType, success, data);
  }, [logger]);

  const logNetworkSwitch = useCallback((
    fromChainId: number, 
    toChainId: number, 
    success: boolean, 
    data?: Record<string, any>
  ) => {
    logger.logNetworkSwitch(fromChainId, toChainId, success, data);
  }, [logger]);

  return {
    logLoginAttempt,
    logLoginSuccess,
    logLoginError,
    logLogout,
    logMessageSign,
    logNetworkSwitch
  };
};

// IDO interaction logging hook
export const useIdoLogger = (): IdoLoggerHook => {
  const logger = useLogger();

  const logIdoPageView = useCallback((
    idoId: string, 
    idoTitle: string, 
    data?: Record<string, any>
  ) => {
    logger.logIdoPageView(idoId, idoTitle, data);
  }, [logger]);

  const logIdoDetailsView = useCallback((
    idoId: string, 
    section: string, 
    data?: Record<string, any>
  ) => {
    logger.logIdoDetailsView(idoId, section, data);
  }, [logger]);

  const logWhitelistAttempt = useCallback((
    idoId: string, 
    data?: Record<string, any>
  ) => {
    logger.logWhitelistAttempt(idoId, data);
  }, [logger]);

  const logWhitelistSuccess = useCallback((
    idoId: string, 
    data?: Record<string, any>
  ) => {
    logger.logWhitelistSuccess(idoId, data);
  }, [logger]);

  const logWhitelistError = useCallback((
    idoId: string, 
    error: Error, 
    data?: Record<string, any>
  ) => {
    logger.logWhitelistError(idoId, error, data);
  }, [logger]);

  return {
    logIdoPageView,
    logIdoDetailsView,
    logWhitelistAttempt,
    logWhitelistSuccess,
    logWhitelistError
  };
};

// Purchase logging hook
export const usePurchaseLogger = (): PurchaseLoggerHook => {
  const logger = useLogger();

  const logPurchaseAttempt = useCallback((
    idoId: string, 
    amount: string, 
    currency: string, 
    data?: Record<string, any>
  ) => {
    logger.logPurchaseAttempt(idoId, amount, currency, data);
  }, [logger]);

  const logPurchaseSuccess = useCallback((
    idoId: string, 
    amount: string, 
    currency: string, 
    txHash: string, 
    data?: Record<string, any>
  ) => {
    logger.logPurchaseSuccess(idoId, amount, currency, txHash, data);
  }, [logger]);

  const logPurchaseError = useCallback((
    idoId: string, 
    amount: string, 
    currency: string, 
    error: Error, 
    data?: Record<string, any>
  ) => {
    logger.logPurchaseError(idoId, amount, currency, error, data);
  }, [logger]);

  const logPurchaseCancel = useCallback((
    idoId: string, 
    reason: string, 
    data?: Record<string, any>
  ) => {
    logger.logPurchaseCancel(idoId, reason, data);
  }, [logger]);

  return {
    logPurchaseAttempt,
    logPurchaseSuccess,
    logPurchaseError,
    logPurchaseCancel
  };
};

// Error logging hook
export const useErrorLogger = (): ErrorLoggerHook => {
  const logger = useLogger();

  const logJavaScriptError = useCallback((
    error: Error, 
    component?: string, 
    action?: string, 
    data?: Record<string, any>
  ) => {
    logger.logJavaScriptError(error, component, action, data);
  }, [logger]);

  const logValidationError = useCallback((
    error: ValidationError, 
    field: string, 
    value: any, 
    data?: Record<string, any>
  ) => {
    logger.logValidationError(error, field, value, data);
  }, [logger]);

  const logNetworkError = useCallback((
    error: NetworkError, 
    endpoint: string, 
    data?: Record<string, any>
  ) => {
    logger.logNetworkError(error, endpoint, data);
  }, [logger]);

  return {
    logJavaScriptError,
    logValidationError,
    logNetworkError
  };
};

// Page view logging hook
export const usePageViewLogger = (): PageViewLoggerHook => {
  const logger = useLogger();

  const logPageView = useCallback((data?: Record<string, any>) => {
    logger.logPageView(undefined, undefined, data);
  }, [logger]);

  const logCustomPageView = useCallback((
    path: string, 
    title: string, 
    data?: Record<string, any>
  ) => {
    logger.logPageView(path, title, data);
  }, [logger]);

  const logRouteChange = useCallback((
    from: string, 
    to: string, 
    data?: Record<string, any>
  ) => {
    logger.logRouteChange(from, to, data);
  }, [logger]);

  return {
    logPageView,
    logCustomPageView,
    logRouteChange
  };
};

// User action logging hook
export const useUserActionLogger = (): UserActionLoggerHook => {
  const logger = useLogger();

  const logButtonClick = useCallback((
    buttonId: string, 
    component: string, 
    data?: Record<string, any>
  ) => {
    logger.logButtonClick(buttonId, component, data);
  }, [logger]);

  const logFormSubmit = useCallback((
    formId: string, 
    component: string, 
    data?: Record<string, any>
  ) => {
    logger.logFormSubmit(formId, component, data);
  }, [logger]);

  const logModalOpen = useCallback((
    modalId: string, 
    trigger: string, 
    data?: Record<string, any>
  ) => {
    logger.logModalOpen(modalId, trigger, data);
  }, [logger]);

  const logModalClose = useCallback((
    modalId: string, 
    trigger: string, 
    data?: Record<string, any>
  ) => {
    logger.logModalClose(modalId, trigger, data);
  }, [logger]);

  return {
    logButtonClick,
    logFormSubmit,
    logModalOpen,
    logModalClose
  };
};

// Network logging hook
export const useNetworkLogger = (): NetworkLoggerHook => {
  const logger = useLogger();

  const logNetworkChange = useCallback((
    chainId: number, 
    networkName: string, 
    data?: Record<string, any>
  ) => {
    logger.logAuthentication('network_switch' as any, undefined, {
      chainId,
      networkName,
      ...data
    });
  }, [logger]);

  const logConnectionStatusChange = useCallback((
    isConnected: boolean, 
    data?: Record<string, any>
  ) => {
    logger.logUserAction('connection_status_change' as any, 'network', {
      isConnected,
      ...data
    });
  }, [logger]);

  return {
    logNetworkChange,
    logConnectionStatusChange
  };
};

// Performance logging hook
export const usePerformanceLogger = (componentName: string) => {
  const logger = useLogger();

  const logCustomPerformance = useCallback((
    metric: string, 
    value: number, 
    unit: string, 
    data?: Record<string, any>
  ) => {
    logger.logPerformance(metric, value, unit, componentName, data);
  }, [logger, componentName]);

  const logComponentMount = useCallback((
    mountTime: number, 
    data?: Record<string, any>
  ) => {
    logger.logComponentMount(componentName, mountTime, data);
  }, [logger, componentName]);

  const logComponentUnmount = useCallback((
    lifetime: number, 
    data?: Record<string, any>
  ) => {
    logger.logComponentUnmount(componentName, lifetime, data);
  }, [logger, componentName]);

  const logApiRequest = useCallback((
    endpoint: string, 
    method: string, 
    duration: number, 
    success: boolean, 
    data?: Record<string, any>
  ) => {
    logger.logApiRequest(endpoint, method, duration, success, data);
  }, [logger]);

  return {
    logCustomPerformance,
    logComponentMount,
    logComponentUnmount,
    logApiRequest
  };
};

// Combined hook for common logging scenarios
export const useCommonLogger = () => {
  const walletLogger = useWalletConnectionLogger();
  const authLogger = useAuthLogger();
  const errorLogger = useErrorLogger();
  const userActionLogger = useUserActionLogger();
  const pageViewLogger = usePageViewLogger();

  return {
    // Wallet connection
    ...walletLogger,
    
    // Authentication
    ...authLogger,
    
    // Error logging
    ...errorLogger,
    
    // User actions
    ...userActionLogger,
    
    // Page views
    ...pageViewLogger,
    
    // Direct logger access
    logger: useLogger()
  };
};

// Hook for automatic page view logging
export const useAutoPageViewLogger = (
  path?: string, 
  title?: string, 
  data?: Record<string, any>
) => {
  const { logPageView } = usePageViewLogger();

  // Log page view on mount
  useEffect(() => {
    logPageView(data);
  }, [logPageView, data]);
};

// Hook for automatic component performance logging
export const useAutoPerformanceLogger = (componentName: string) => {
  const { logComponentMount, logComponentUnmount } = usePerformanceLogger(componentName);
  const mountTimeRef = useRef<number>();

  useEffect(() => {
    const mountTime = Date.now();
    mountTimeRef.current = mountTime;
    
    logComponentMount(mountTime);

    return () => {
      if (mountTimeRef.current) {
        const lifetime = Date.now() - mountTimeRef.current;
        logComponentUnmount(lifetime);
      }
    };
  }, [logComponentMount, logComponentUnmount]);
};
